import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import {
  BadgeCheck,
  CheckCircle2,
  Users,
  BarChart3,
  Landmark,
  BookOpen,
  Building2,
  HeartHandshake,
  Wallet,
  GraduationCap,
  Home as HomeIcon,
  Stethoscope,
  Rocket,
  Shield,
  Lock,
  Quote,
  ChevronRight,
} from "lucide-react";

const programIcons = [Users, GraduationCap, Building2, HomeIcon, Stethoscope, Rocket];

function Field({ label, children, error, testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
      {children}
      {error && <span data-testid={`${testId}-error`} className="text-xs text-rose-600">{error}</span>}
    </label>
  );
}

export default function Home() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const programs = t("programs.items") || [];
  const modules = t("literacy.modules") || [];
  const stats = t("trust.stats") || [];
  const badges = t("trust.badges") || [];
  const testimonials = t("testimonials.items") || [];
  const faq = t("faq.items") || [];

  const [newsForm, setNewsForm] = useState({ name: "", email: "" });
  const [newsErr, setNewsErr] = useState({});

  const submitNewsletter = (e) => {
    e.preventDefault();
    const err = {};
    if (!newsForm.name.trim()) err.name = t("form.requiredError");
    if (!newsForm.email.trim()) err.email = t("form.requiredError");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsForm.email)) err.email = t("form.emailError");
    setNewsErr(err);
    if (Object.keys(err).length === 0) navigate("/thank_you");
  };

  return (
    <div className="bg-slate-50">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A2540] text-white">
        <div className="absolute inset-0 -z-0">
          <img
            src="/images/hero_consultant.jpg"
            alt="Consultor financiero"
            className="h-full w-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-[#0A2540]/95 to-[#0A2540]/60" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8 lg:py-28">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-400/40">
            <BadgeCheck className="h-3.5 w-3.5" /> {t("hero.eyebrow")}
          </span>
          <h1 className="mx-auto mt-5 max-w-3xl text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl lg:text-6xl">
            {t("hero.title")}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
            {t("hero.subtitle")}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="#newsletter"
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-emerald-400"
            >
              {t("hero.primaryCta")} <ChevronRight className="h-4 w-4" />
            </a>
            <a
              href="#programs"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              {t("hero.secondaryCta")}
            </a>
          </div>
          <ul className="mx-auto mt-10 grid max-w-3xl gap-2 text-sm text-slate-200 sm:grid-cols-3">
            {[t("hero.badge1"), t("hero.badge2"), t("hero.badge3")].map((b, i) => (
              <li key={i} className="flex items-start justify-center gap-2 text-left">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-6 sm:px-6 lg:px-8">
          {badges.map((b, i) => {
            const Icon = [Shield, Landmark, Lock, BookOpen][i] || Shield;
            return (
              <div key={i} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-700">
                <Icon className="h-4 w-4 text-emerald-700" />
                <span>{b}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="programs" className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-800">{t("programs.eyebrow")}</span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {t("programs.title")}
            </h2>
            <p className="mt-3 text-base text-slate-600">{t("programs.subtitle")}</p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {programs.map((p, i) => {
              const Icon = programIcons[i % programIcons.length];
              return (
                <article
                  key={i}
                  data-testid={`program-card-${i}`}
                  className="flex flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="rounded bg-slate-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                      {p.tag}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.description}</p>
                  <a
                    href="#newsletter"
                    data-testid={`program-cta-${i}`}
                    className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                  >
                    {t("hero.primaryCta")} <ChevronRight className="h-4 w-4" />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* LITERACY */}
      <section id="literacy" className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-800">{t("literacy.eyebrow")}</span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {t("literacy.title")}
            </h2>
            <p className="mt-3 text-base text-slate-600">{t("literacy.subtitle")}</p>
            <ol className="mt-8 space-y-5">
              {modules.map((m, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-[#0A2540] text-sm font-bold text-white">
                    {m.step}
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{m.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{m.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="relative">
            <img
              src="/images/office_meeting.jpg"
              alt="Consultora explicando finanzas"
              className="h-[420px] w-full rounded-2xl object-cover shadow-xl ring-1 ring-slate-900/5"
            />
            <div className="absolute -bottom-6 -left-6 hidden max-w-[240px] rounded-xl bg-white p-4 shadow-lg ring-1 ring-slate-200 sm:block">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-700" />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">2026</span>
              </div>
              <p className="mt-2 text-sm font-semibold text-slate-900">+37% ahorro promedio</p>
              <p className="text-xs text-slate-500">tras 6 meses de acompañamiento</p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-800">{t("trust.eyebrow")}</span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {t("trust.title")}
            </h2>
            <p className="mt-3 text-base text-slate-600">{t("trust.subtitle")}</p>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stats.map((s, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-6">
                <div className="text-3xl font-extrabold text-[#0A2540] sm:text-4xl">{s.value}</div>
                <div className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-800">{t("testimonials.eyebrow")}</span>
          <h2 className="mt-3 max-w-3xl text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            {t("testimonials.title")}
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {testimonials.map((tst, i) => (
              <figure key={i} className="flex flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`testimonial-${i}`}>
                <Quote className="h-6 w-6 text-emerald-500" />
                <blockquote className="mt-4 text-sm leading-relaxed text-slate-700">"{tst.quote}"</blockquote>
                <figcaption className="mt-5 border-t border-slate-100 pt-4">
                  <div className="text-sm font-semibold text-slate-900">{tst.name}</div>
                  <div className="text-xs text-slate-500">{tst.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-800">{t("faq.eyebrow")}</span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{t("faq.title")}</h2>
          <div className="mt-10 divide-y divide-slate-200 rounded-xl border border-slate-200">
            {faq.map((f, i) => (
              <details key={i} className="group open:bg-slate-50" data-testid={`faq-item-${i}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4">
                  <span className="text-sm font-semibold text-slate-900 sm:text-base">{f.q}</span>
                  <ChevronRight className="h-4 w-4 text-slate-500 group-open:rotate-90" />
                </summary>
                <div className="px-5 pb-5 text-sm leading-relaxed text-slate-600">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-[#0A2540] text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-400/40">
              <HeartHandshake className="h-3.5 w-3.5" /> {t("hero.eyebrow")}
            </span>
            <h2 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              {t("form.newsletterTitle")}
            </h2>
            <p className="mt-3 text-base text-slate-200">{t("form.newsletterSubtitle")}</p>
            <ul className="mt-6 grid gap-2 text-sm text-slate-200 sm:grid-cols-2">
              <li className="flex items-start gap-2"><Wallet className="mt-0.5 h-4 w-4 text-emerald-300" />{t("footer.programsLink")}</li>
              <li className="flex items-start gap-2"><BookOpen className="mt-0.5 h-4 w-4 text-emerald-300" />{t("footer.literacyLink")}</li>
            </ul>
          </div>
          <form onSubmit={submitNewsletter} className="rounded-xl bg-white p-6 text-slate-900 shadow-2xl ring-1 ring-white/10 sm:p-7" data-testid="newsletter-form" noValidate>
            <Field label={t("form.nameLabel")} error={newsErr.name} testId="newsletter-name">
              <input
                data-testid="newsletter-name-input"
                value={newsForm.name}
                onChange={(e) => setNewsForm({ ...newsForm, name: e.target.value })}
                placeholder={t("form.namePlaceholder")}
                className="rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
              />
            </Field>
            <div className="mt-4">
              <Field label={t("form.emailLabel")} error={newsErr.email} testId="newsletter-email">
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  value={newsForm.email}
                  onChange={(e) => setNewsForm({ ...newsForm, email: e.target.value })}
                  placeholder={t("form.emailPlaceholder")}
                  className="rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
                />
              </Field>
            </div>
            <button
              type="submit"
              data-testid="newsletter-submit-button"
              className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-emerald-500 px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-emerald-400"
            >
              {t("form.newsletterSubmit")}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
